import { Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { LOADING_TYPES, useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { getInitialDynamicFieldValues, useDynamicFields } from '../../utils/dynamicFields';
import getAvailableDeliveryTypes from '../../utils/getAvailableDeliveryTypes';
import getCarriers from '../../utils/getCarriers';
import { useModal } from '../../utils/modal';
import snakeCase from '../../utils/snakeCase';
import { getCustomerType, pushTrackingEvent } from '../../utils/tracking';
import useIsMobileDevice from '../../utils/useIsMobileDevice';
import useOnScreen, { scrollIntoView } from '../../utils/useOnScreen';
import useThreadMetrix from '../../utils/useThreadMetrix';
import { useUnzerResource } from '../../utils/useUnzer';
import FixedScrollNavigation from '../atoms/FixedScrollNavigation';
import MainHeadline from '../atoms/MainHeadline';
import { ModalProps } from '../atoms/Modal';
import SubHeadline from '../atoms/SubHeadline';
import AddressOverview from '../molecules/AddressOverview';
import BillingAddress from '../molecules/BillingAddress';
import CarrierOptions from '../molecules/CarrierOptions';
import DiscountCodes from '../molecules/DiscountCodes';
import DynamicFieldArea from '../molecules/DynamicFieldArea';
import GiftCardList from '../molecules/GiftCardList';
import LoyaltyProgram from '../molecules/LoyaltyProgram';
import Navigation from '../molecules/Navigation';
import { showDefaultError } from '../molecules/Notifications';
import PaymentOptions from '../molecules/PaymentOptions';
import getCustomNavigationButton from '../molecules/PaymentOptions/getCustomNavigationButton';
import PriceSummary from '../molecules/PriceSummary';
import PromotionEngineError from '../molecules/PromotionEngineError';
import AdyenMbwayPayment from '../payment/AdyenMbwayPayment';
import { useAmazonPayment } from '../payment/AmazonPayment';
import ArvatoPayment from '../payment/ArvatoPayment';
import ComputopPayment from '../payment/ComputopPayment';
import { useKlarnaPayment } from '../payment/KlarnaPayment';
import Review from '../payment/Review';
import Basket from './Basket';
import redirect from '../../utils/redirect';

const ADYEN_KEYS = [
  'adyen_klarna_installments',
  'adyen_klarna_paylater',
  'adyen_klarna_paynow',
  'adyen_apple_pay',
  'adyen_google_pay',
  'adyen_paypal',
  'adyen_twint',
  'spay_adyen_mobilepay',
  'spay_adyen_twint',
  'spay_adyen_google_pay',
  'spay_adyen_apple_pay',
  'adyen_online_banking_pl',
  'spay_adyen_online_banking_pl',
  'adyen_creditcard',
  'spay_adyen_credit_card',
  'spay_adyen_creditcard_bc',
  'adyen_creditcard_bc',
  'spay_adyen_blik',
  'spay_adyen_ideal'
];

const ARVATO_KEYS: (string | undefined)[] = ['arvato_creditcard', 'arvato_ideal'];
const COMPUTOP_KEYS: (string | undefined)[] = [
  'computop_creditcard',
  'computop_creditcard_mastercard',
  'computop_creditcard_visa',
  'computop_maestro',
  'computop_bancontact'
];

const KLARNA_KEYS = [
  'klarna_paylater',
  'klarna_paynow_direct_debit',
  'klarna_paynow_direct_bank_transfer',
  'klarna_installments',
  'klarna_paynow',
  'spay_klarna_paynow',
  'spay_klarna_paylater',
  'spay_klarna_payovertime'
];

const UNZER_KEYS = ['unzer_creditcard', 'unzer_ideal', 'unzer_installment'];

const PaymentStep = () => {
  const {
    checkoutState,
    confirmOrder,
    removePromotions,
    setPollingEnabled,
    isLoading,
    setLoading,
    scrollRefs: { basketRef, navigationRef },
    adyenSpecificationPayloadRef,
    filledSlots
  } = useCheckoutState();
  const navigate = useNavigate();
  const { toggleModal } = useModal();
  const { submitDynamicFieldArea } = useDynamicFields();

  const {
    customer,
    integrations: { promotion },
    orderState: { addresses, shipping, memberships, payment, basket, id: orderId },
    orderOptions,
    orderConfirmation,
    isExpressCheckout
  } = checkoutState;

  const selectedCarrier = shipping?.selected?.[0];
  const isNavigationOnViewport = useOnScreen(navigationRef);
  const isCollectionPoint = !!addresses?.shipping?.collectionPoint;
  const [isEditingBillingAddress, setEditingBillingAddress] = useState(isCollectionPoint && !addresses?.billing);

  const isNewUser = getCustomerType(checkoutState) !== 'existing';
  const areShippingOptionsAvailable = !!orderOptions?.shipping?.homeDelivery?.length;
  const isEnabledAndPaymentPosition =
    config.carrierOptions.isCollapsible.enabled && config.carrierOptions.position === 'payment';
  const [isEditingCarriers, setEditingCarriers] = useState(
    isEnabledAndPaymentPosition ? isNewUser || (areShippingOptionsAvailable && !selectedCarrier) : false
  );

  const selectedPaymentOption = payment?.selected?.[0];
  const selectedGiftCard = payment?.giftCards?.[0];
  const selectedPaymentOptionKey = selectedPaymentOption?.paymentOptionKey ?? selectedGiftCard?.paymentOptionKey;
  const selectedRecurringPaymentOptionId = selectedPaymentOption?.recurringPaymentOptionId;

  const isMobile = useIsMobileDevice();

  const isRecurring = !!selectedRecurringPaymentOptionId;
  const canBeRecurring = selectedPaymentOption?.configuration?.isRecurring;
  const { id, token } = selectedPaymentOption?.configuration?.initializationData?.deviceIdent ?? {};

  useThreadMetrix(id, token);

  useEffect(() => {
    if (selectedPaymentOptionKey) {
      pushTrackingEvent({
        event: 'feature',
        action: 'click',
        name: 'payment_method_preselect',
        label: `payment_method_preselect|${selectedPaymentOptionKey}`
      });
    }
  }, []);


  const isPostConfirmationActionRequired = (): boolean => (KLARNA_KEYS.includes(selectedPaymentOptionKey!) && !isRecurring);

  const executePostConfirmationAction = async (values: typeof initialValues): Promise<void> => {
    const redirectData = orderConfirmation?.redirect;

    if (isExpressCheckout && redirectData) {
      redirect(redirectData);

      return;
    }

    try {
      const authorizationId = await authorizeKlarnaPayment(canBeRecurring ? values.storeKlarnaPaymentMethod : false);

      if (redirectData) {
        const hasParameters = redirectData.url.includes('?');
        const tokenString = `authorizationToken=${authorizationId}`
        redirectData.url += hasParameters ? `&${tokenString}` : `?${tokenString}`;

        redirect(redirectData);
      }

    } catch (e: any) {
      if (e?.message !== 'Klarna risk rejection') {
        console.error(e);
        showDefaultError();
        pushTrackingEvent({
          event: 'feature',
          action: 'impression',
          name: snakeCase(selectedPaymentOptionKey!),
          label: `error|${snakeCase(selectedPaymentOptionKey!)}|${snakeCase(e?.message)}`
        });
      }
      setLoading(LOADING_TYPES.off);
    }
  };

  useEffect(() => {
    if (!orderConfirmation?.redirect) {
      return;
    }

    if (isPostConfirmationActionRequired()) {
      executePostConfirmationAction(submittedValues);
      return;
    }

    const isComputopModal = !isMobile && COMPUTOP_KEYS.includes(selectedPaymentOptionKey);
    const isArvatoModal = ARVATO_KEYS.includes(selectedPaymentOptionKey);
    const isAdyenMbwayModal = selectedPaymentOptionKey === 'adyen_mbway';

    let modalConfig: ModalProps = { children: <></>, isDifferentTree: IS_WEB_COMPONENT && !import.meta.env.DEV };
    const onModalClose = () => {
      toggleModal();
      setPollingEnabled(true);
      setLoading(LOADING_TYPES.off);
    };

    if (isComputopModal) {
      modalConfig = {
        ...modalConfig,
        children: <ComputopPayment onError={onModalClose} />,
        contentClassName: 'modal__content--computop'
      };
    } else if (isArvatoModal) {
      modalConfig = { ...modalConfig, children: <ArvatoPayment />, onClose: onModalClose };
    } else if (isAdyenMbwayModal) {
      modalConfig = {
        ...modalConfig,
        children: <AdyenMbwayPayment />,
        contentClassName: 'mbway',
        onClose: onModalClose
      };
    }

    toggleModal(modalConfig);
  }, [orderConfirmation?.redirect]);

  const onPromotionErrorSubmit = async () => {
    await removePromotions();
    toggleModal();
  };

  const showPromotionEngineValidationErrorModal = () => {
    const modalConfig = {
      children: <PromotionEngineError onSubmit={onPromotionErrorSubmit} onClose={() => toggleModal()} />,
      onEscapeKey: () => toggleModal(),
      onClickOutside: () => toggleModal(),
      onClose: () => toggleModal()
    };

    toggleModal(modalConfig);
  };

  useEffect(() => {
    if (basket.promotionEngineValidationError) {
      showPromotionEngineValidationErrorModal();
    }
  }, [basket.promotionEngineValidationError]);

  const basketTotal = basket.total.costCapture ?? basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard;
  // const basketSubtotal = basket.total.costWithTaxWithoutVouchersWithoutServiceCosts;
  const basketShippingCost = basket.total.costShippingServiceCosts;

  const { authorizeKlarnaPayment } = useKlarnaPayment();
  const { redirectToAmazon } = useAmazonPayment();
  const { createResource } = useUnzerResource();

  const locationSearch = new URLSearchParams(window.location.search);
  const isAmazonPayReviewPage = !!locationSearch.get('isAmazonPayReviewPage');
  const isPaypalReviewPage = !!locationSearch.get('isReviewPage');
  const isExpressCheckoutReviewPage = !!checkoutState.isExpressCheckout;

  if (!customer) {
    return <Navigate to="/auth" replace />;
  } else if (!addresses?.shipping && !isPaypalReviewPage) {
    return <Navigate to="/shipping" replace />;
  }

  const { carrierKey, carrierGroupKey, deliveryDate, shippingOptionKey } = selectedCarrier ?? {
    carrierKey: '',
    carrierGroupKey: '',
    deliveryDate: '',
    shippingOptionKey: ''
  };

  const isDifferentAddress = !!addresses?.billing && addresses?.billing.hash !== addresses?.shipping?.hash;

  const initialValues = {
    carrier: { carrierKey, carrierGroupKey, deliveryDate, shippingOptionKey },
    billingAddressType: isDifferentAddress || isCollectionPoint ? 'differentAddress' : 'sameAddress',
    paymentMethod: selectedRecurringPaymentOptionId ?? selectedPaymentOptionKey ?? '',
    sepaDirectDebit: { iban: '', accountOwner: '' },
    creditCard: { issuer: '', number: '', holder: '', expiration: '', cvv: '' },
    adyenPaypal: { hasError: false },
    adyenBlik: { isValid: false },
    adyenCreditcard: { isValid: false },
    adyenBancontactCard: { isValid: false },
    adyenOnlineBankingPl: { isValid: false },
    unzerCreditcard: { isValid: false },
    unzerIdeal: { isValid: false },
    unzerInstallment: { isValid: false, reason: 'plan' },
    storeKlarnaPaymentMethod: true,
    storeUnzerPaymentMethod: false,
    ...getInitialDynamicFieldValues(checkoutState, 'paymentOptions', 'payment', 'paymentBelowMobileBasket')
  };

  let submittedValues: typeof initialValues = initialValues;

  const onSubmit = async (values: typeof initialValues, { setFieldValue }: FormikHelpers<typeof initialValues>) => {
    submittedValues = values;
    window.ayCheckoutFunctions?.dispatchEvent?.(
      new CustomEvent('intentConfirmation', { detail: { orderId, customerId: customer?.data.id } })
    );

    if (basket.promotionEngineValidationError) {
      showPromotionEngineValidationErrorModal();
      return;
    }

    setLoading(LOADING_TYPES.paymentSubmit);
    setPollingEnabled(false);

    const conditionValues = {
      paymentOptionKey: selectedPaymentOptionKey,
      carrierGroupKey: getCarriers(checkoutState.orderState),
      shippingOptionKey: values.carrier.shippingOptionKey
    };

    const currentUrlObj = new URL(window.location.href);
    currentUrlObj.hash = '';
    const currentUrl = currentUrlObj.toString();

    let hasRedirect = false;
    let unzerResourceId = '';

    try {
      await submitDynamicFieldArea({ area: 'paymentOptions', conditionValues }, values);
      await submitDynamicFieldArea({ area: 'payment', conditionValues }, values);
      await submitDynamicFieldArea({ area: 'paymentBelowMobileBasket', conditionValues }, values);

      if (UNZER_KEYS.includes(selectedPaymentOptionKey!) && !isRecurring) {
        unzerResourceId = await createResource(selectedPaymentOptionKey!);
        if (!unzerResourceId) {
          setLoading(LOADING_TYPES.off);
          return;
        }
      } else if (selectedPaymentOptionKey === 'amazon_pay') {
        await redirectToAmazon({ currentUrl });
        setLoading(LOADING_TYPES.off);
        return;
      }

      const canBeStored = !isRecurring && canBeRecurring;

      const specifications = {
        adyen: adyenSpecificationPayloadRef.current,
        klarna: {
          ...(canBeStored && { storePaymentMethod: values.storeKlarnaPaymentMethod })
        },
        ratepay_directdebit: { iban: values.sepaDirectDebit.iban },
        unzer: {
          paymentTypeId: unzerResourceId,
          ...(values.storeUnzerPaymentMethod && { storePaymentMethod: true })
        }
      };

      const paymentKey =
        ADYEN_KEYS.includes(selectedPaymentOptionKey!) ? 'adyen'
        : KLARNA_KEYS.includes(selectedPaymentOptionKey!) ? 'klarna'
        : UNZER_KEYS.includes(selectedPaymentOptionKey!) && !isRecurring ? 'unzer'
        : selectedPaymentOptionKey!;

      const result = await confirmOrder({
        paymentOptionKey: selectedPaymentOptionKey!,
        recurringPaymentOptionId: selectedRecurringPaymentOptionId,
        currentUrl,
        specification: specifications[paymentKey as keyof typeof specifications]
      });

      hasRedirect = result.hasRedirect;
      if (selectedPaymentOptionKey === 'adyen_paypal' && result.hasNotification) {
        setFieldValue('adyenPaypal.hasError', true);
        setTimeout(() => {
          setFieldValue('adyenPaypal.hasError', false);
        }, 4000);
      }
    } catch (e: any) {
      if (e?.message !== 'Klarna risk rejection') {
        console.error(e);
        showDefaultError();
        pushTrackingEvent({
          event: 'feature',
          action: 'impression',
          name: snakeCase(selectedPaymentOptionKey!),
          label: `error|${snakeCase(selectedPaymentOptionKey!)}|${snakeCase(e?.message)}`
        });
      }
    }

    if (!hasRedirect) {
      setPollingEnabled(true);
    }

    if (!hasRedirect && ![...COMPUTOP_KEYS, ...ARVATO_KEYS].includes(selectedPaymentOptionKey)) {
      setLoading(LOADING_TYPES.off);
    }
  };

  const isNavigationDisabled = ({
    paymentMethod,
    adyenBancontactCard,
    adyenCreditcard,
    adyenOnlineBankingPl,
    adyenPaypal,
    unzerCreditcard,
    unzerIdeal,
    unzerInstallment,
    adyenBlik
  }: typeof initialValues) => {
    const isPaymentDataInvalid =
      (['spay_adyen_creditcard_bc', 'adyen_creditcard_bc'].includes(selectedPaymentOptionKey!) &&
        !adyenBancontactCard.isValid) ||
      (['spay_adyen_credit_card', 'adyen_creditcard'].includes(selectedPaymentOptionKey!) &&
        !adyenCreditcard.isValid) ||
      (['adyen_online_banking_pl', 'spay_adyen_online_banking_pl'].includes(paymentMethod) &&
        !adyenOnlineBankingPl.isValid) ||
      (paymentMethod === 'unzer_creditcard' && !unzerCreditcard.isValid) ||
      (paymentMethod === 'unzer_ideal' && !unzerIdeal.isValid) ||
      (paymentMethod === 'spay_adyen_blik' && !adyenBlik.isValid);

    if (paymentMethod === 'unzer_installment') {
      if (!unzerInstallment.isValid) {
        return `payment.navigation.errors.unzerInstallment.${unzerInstallment.reason}`;
      }
    }

    if (adyenPaypal.hasError) {
      return 'payment.notifications.riskAssessmentDenied';
    }
    if (isPaymentDataInvalid) {
      return 'payment.navigation.errors.paymentDataInvalid';
    } else if (isEditingBillingAddress) {
      return 'payment.navigation.errors.editingBillingAddress';
    } else if (!paymentMethod) {
      return 'payment.navigation.errors.noPaymentMethod';
    }

    return undefined;
  };

  const areGiftCardsEnabled = config.features.giftCards.enabled;
  const arePromotionsEnabled = promotion?.codes?.enabled;
  const areVouchersEnabled = config.features.vouchers.enabled;
  const discountsEnabled = arePromotionsEnabled || areVouchersEnabled;
  const showBasket = isMobile && config.basket.position === 'bottom';
  const discountPaymentPosition = config.features.discountCodes.position === 'payment';
  const discountInMobileView = isMobile && config.basket.position !== 'bottom';
  const showDiscount = (discountPaymentPosition || discountInMobileView) && discountsEnabled;
  const voucherAndGiftCardHeadingSeparated =
    !discountPaymentPosition && (!isMobile || config.basket.position === 'bottom');

  const areCollectionPointsAvailable =
    !!orderOptions?.shipping && getAvailableDeliveryTypes(orderOptions.shipping).includes('collectionPoint');
  const canSuggestCP = !isCollectionPoint && areCollectionPointsAvailable;

  const hidePaymentSection =
    basket.total.costCapture === 0 &&
    memberships?.activated?.[0].redeemedPoints &&
    memberships?.activated?.[0].redeemedPoints > 0;

  const voucherAndGiftCardHeading =
    voucherAndGiftCardHeadingSeparated ?
      areGiftCardsEnabled ? 'payment.giftCard.headline'
      : undefined
    : areGiftCardsEnabled && discountsEnabled ? 'payment.giftCardAndVoucher.headline'
    : areGiftCardsEnabled ? 'payment.giftCard.headline'
    : 'payment.voucher.headline';

  const isReviewPage = isAmazonPayReviewPage || isPaypalReviewPage || isExpressCheckoutReviewPage;
  if (isReviewPage) {
    return (
      <Review
        showBasket={showBasket}
        isPpx={!!isPaypalReviewPage}
        isExpressCheckoutReviewPage={!!isExpressCheckoutReviewPage}
        isAmazonPayReviewPage={!!isAmazonPayReviewPage}
      />
    );
  }

  const isLoyaltyProgramEnabled =
    config.features.loyaltyProgram.enabled &&
    !(config.features.loyaltyProgram.registration.disableForGuest && customer.flags?.isGuest);
  const navigateToCP = () => navigate('/shipping', { state: { preselectedDeliveryType: 'collectionPoint' } });

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ values }) => (
        <div className="form payment-form">
          <MainHeadline>
            <FormattedMessage id="payment.headline" />
          </MainHeadline>
          <AddressOverview
            addressType="shipping"
            showTitle
            onChangeClick={() => navigate('/shipping')}
            navigateToCp={
              config.deliveryOptions.suggestCollectionPoint.enabled && canSuggestCP ? navigateToCP : undefined
            }
          />
          {config.carrierOptions.position === 'payment' && !isCollectionPoint && (
            <CarrierOptions
              isEditingCarriers={isEditingCarriers}
              setEditingCarriers={setEditingCarriers}
              navigateToCp={
                config.carrierOptions.suggestCollectionPoint.enabled && canSuggestCP ? navigateToCP : undefined
              }
            />
          )}
          <BillingAddress
            isCollectionPoint={isCollectionPoint}
            isEditingBillingAddress={isEditingBillingAddress}
            setEditingBillingAddress={setEditingBillingAddress}
          />
          {!hidePaymentSection && <PaymentOptions />}
          <slot name="slot_replace_loyalty_program">{isLoyaltyProgramEnabled && <LoyaltyProgram />}</slot>
          {voucherAndGiftCardHeading && (
            <SubHeadline>
              <FormattedMessage id={voucherAndGiftCardHeading} />
            </SubHeadline>
          )}
          {areGiftCardsEnabled && <GiftCardList />}
          {showDiscount && <DiscountCodes />}
          <DynamicFieldArea
            area="payment"
            conditionValues={{
              paymentOptionKey: selectedPaymentOptionKey,
              carrierGroupKey: getCarriers(checkoutState.orderState),
              shippingOptionKey: values.carrier.shippingOptionKey
            }}
          />
          {showBasket && <Basket />}
          <PriceSummary className="price-summary__box" total={basketTotal} shippingCost={basketShippingCost} />
          <DynamicFieldArea
            area="paymentBelowMobileBasket"
            conditionValues={{
              paymentOptionKey: selectedPaymentOptionKey,
              carrierGroupKey: getCarriers(checkoutState.orderState),
              shippingOptionKey: values.carrier.shippingOptionKey
            }}
          />
          <Navigation
            ref={navigationRef}
            step="payment"
            nextStepLabel={<FormattedMessage id="payment.payNow" />}
            onPreviousStepClick={() => navigate('/shipping')}
            isLoading={isLoading}
            disabledInformation={isNavigationDisabled(values)}
            customButton={getCustomNavigationButton(selectedPaymentOptionKey)}
          />
          {DEBUG && <pre style={{ marginTop: 60 }}>{JSON.stringify(values, null, 2)}</pre>}
          {!isNavigationOnViewport && (
            <FixedScrollNavigation
              label={<FormattedMessage id="payment.continue" />}
              onClick={() => scrollIntoView(isMobile ? navigationRef : basketRef)}
              isLoading={isLoading}
              disabledInformation={isNavigationDisabled(values)}
            />
          )}
        </div>
      )}
    </Formik>
  );
};

export default PaymentStep;
